// Tempo- und Taktart-Spur (Pro Tools: Tempo- und Meter-Lineal).
// Zeit in Sekunden, musikalische Position in Viertelnoten („Beats“) ab dem Songanfang.
// Tempowechsel liegen auf Beats, Taktwechsel auf Takten. Ohne Wechsel gilt project.bpm und project.beatsPerBar wie bisher.
// Audio ist zeitbasiert (Sekunden), MIDI, Marker, Akkorde und Loop folgen den Takten (siehe retime()).

export interface TempoEvent { /** Position in Viertelnoten ab Songanfang (> 0) */ beat: number; bpm: number }
export interface MeterEvent { /** Takt, ab dem die Taktart gilt (0 = erster Takt) */ bar: number; num: number; den: number }
export interface TempoSource { bpm: number; beatsPerBar: number; meterDen?: number; tempos?: TempoEvent[]; meters?: MeterEvent[] }

interface TSeg { beat: number; sec: number; spb: number }
interface MSeg { bar: number; beat: number; num: number; den: number; barLen: number }
interface TMap { t: TSeg[]; m: MSeg[] }

const cache = new WeakMap<object, { key: string; map: TMap }>();
const keyOf = (p: TempoSource) => `${p.bpm}|${p.beatsPerBar}|${p.meterDen ?? 4}|${JSON.stringify(p.tempos ?? [])}|${JSON.stringify(p.meters ?? [])}`;

function build(p: TempoSource): TMap {
  const ev = [{ beat: 0, bpm: p.bpm }, ...(p.tempos ?? []).filter((e) => e.beat > 1e-9 && e.bpm > 0)].sort((a, b) => a.beat - b.beat);
  const t: TSeg[] = [];
  for (const e of ev) {
    const prev = t[t.length - 1];
    if (prev && Math.abs(prev.beat - e.beat) < 1e-9) { prev.spb = 60 / e.bpm; continue; }
    const sec = prev ? prev.sec + (e.beat - prev.beat) * prev.spb : 0;
    t.push({ beat: e.beat, sec, spb: 60 / e.bpm });
  }
  const mev = [{ bar: 0, num: p.beatsPerBar, den: p.meterDen ?? 4 }, ...(p.meters ?? []).filter((e) => e.bar > 0 && e.num > 0 && e.den > 0)].sort((a, b) => a.bar - b.bar);
  const m: MSeg[] = [];
  for (const e of mev) {
    const bar = Math.round(e.bar), prev = m[m.length - 1], barLen = e.num * 4 / e.den;
    if (prev && prev.bar === bar) { prev.num = e.num; prev.den = e.den; prev.barLen = barLen; continue; }
    const beat = prev ? prev.beat + (bar - prev.bar) * prev.barLen : 0;
    m.push({ bar, beat, num: e.num, den: e.den, barLen });
  }
  return { t, m };
}
function mapOf(p: TempoSource): TMap {
  const k = keyOf(p), c = cache.get(p);
  if (c && c.key === k) return c.map;
  const map = build(p);
  cache.set(p, { key: k, map });
  return map;
}
const segByBeat = (t: TSeg[], b: number) => { let s = t[0]; for (const x of t) { if (x.beat <= b + 1e-9) s = x; else break; } return s; };
const segBySec = (t: TSeg[], sec: number) => { let s = t[0]; for (const x of t) { if (x.sec <= sec + 1e-9) s = x; else break; } return s; };
const mByBar = (m: MSeg[], bar: number) => { let s = m[0]; for (const x of m) { if (x.bar <= bar + 1e-9) s = x; else break; } return s; };
const mByBeat = (m: MSeg[], b: number) => { let s = m[0]; for (const x of m) { if (x.beat <= b + 1e-9) s = x; else break; } return s; };

/** Hat das Projekt Tempo- oder Taktwechsel? */
export const hasTempoMap = (p: TempoSource) => !!(p.tempos?.length || p.meters?.length);
/** Sekunde zur Viertel-Position */
export function secAt(p: TempoSource, beat: number): number { const s = segByBeat(mapOf(p).t, beat); return s.sec + (beat - s.beat) * s.spb; }
/** Viertel-Position zur Sekunde */
export function beatAt(p: TempoSource, sec: number): number { const s = segBySec(mapOf(p).t, sec); return s.beat + (sec - s.sec) / s.spb; }
/** Sekunden pro Viertel an dieser Stelle */
export function spbAt(p: TempoSource, sec: number): number { return segBySec(mapOf(p).t, sec).spb; }
export function bpmAt(p: TempoSource, sec: number): number { return Math.round(6000 / spbAt(p, sec)) / 100; }
/** Viertel zwischen zwei Zeitpunkten */
export const beatsBetween = (p: TempoSource, a: number, b: number) => beatAt(p, b) - beatAt(p, a);
/** Sekunde, die n Viertel nach sec liegt */
export const secAfterBeats = (p: TempoSource, sec: number, beats: number) => secAt(p, beatAt(p, sec) + beats);

/** Taktart eines Takts (0-basiert) */
export function meterAtBar(p: TempoSource, bar: number): { num: number; den: number; barLen: number } { const m = mByBar(mapOf(p).m, bar); return { num: m.num, den: m.den, barLen: m.barLen }; }
/** Taktart an einer Zeit */
export function meterAt(p: TempoSource, sec: number) { const m = mByBeat(mapOf(p).m, beatAt(p, sec)); return { num: m.num, den: m.den, barLen: m.barLen }; }
/** Viertel-Position, an der ein Takt (0-basiert, auch gebrochen) beginnt */
export function barToBeat(p: TempoSource, bar: number): number { const m = mByBar(mapOf(p).m, Math.floor(bar)); return m.beat + (bar - m.bar) * m.barLen; }
/** Takt (0-basiert, gebrochen) an einer Viertel-Position */
export function beatToBar(p: TempoSource, beat: number): number { const m = mByBeat(mapOf(p).m, beat); return m.bar + (beat - m.beat) / m.barLen; }
export const secOfBar = (p: TempoSource, bar: number) => secAt(p, barToBeat(p, bar));
export const barAtSec = (p: TempoSource, sec: number) => beatToBar(p, beatAt(p, sec));

/** Takt | Schlag | Tick (1-basiert, Tick 0–999 eines Schlags in der Notenwert-Einheit der Taktart) */
export function bbt(p: TempoSource, sec: number): { bar: number; beat: number; tick: number; num: number; den: number } {
  const b = beatAt(p, sec), m = mByBeat(mapOf(p).m, b);
  const inBar = b - m.beat, barIdx = m.bar + Math.floor(inBar / m.barLen + 1e-9);
  const within = inBar - (barIdx - m.bar) * m.barLen, unit = 4 / m.den;
  let beat = Math.floor(within / unit + 1e-9), tick = Math.round(((within / unit) - beat) * 1000);
  if (tick >= 1000) { tick = 0; beat++; }
  return { bar: barIdx + 1, beat: Math.min(beat, m.num - 1) + 1, tick: Math.max(0, tick), num: m.num, den: m.den };
}
/** Sekunde zu Takt | Schlag | Tick (1-basiert) */
export function secOfBBT(p: TempoSource, bar: number, beat = 1, tick = 0): number {
  const m = meterAtBar(p, bar - 1);
  return secAt(p, barToBeat(p, bar - 1) + ((beat - 1) + tick / 1000) * (4 / m.den));
}

// ---------------------------------------------------------------------------
// Raster
/** Rasterweite in Vierteln; 'bar' = ein Takt (je nach Taktart) */
export const GRID_BEATS: Record<string, number> = { '1/2': 2, '1/4': 1, '1/8': 0.5, '1/16': 0.25, '1/32': 0.125, '1/8T': 1 / 3, '1/16T': 1 / 6 };
/** Rasterlinien zwischen a und b (Sekunden). Unterteilungen beginnen in jedem Takt neu. */
export function gridLines(p: TempoSource, a: number, b: number, grid: string, max = 4000): number[] {
  const out: number[] = [];
  let bar = Math.max(0, Math.floor(barAtSec(p, Math.max(0, a))));
  const step = GRID_BEATS[grid];
  while (out.length < max) {
    const b0 = barToBeat(p, bar), s0 = secAt(p, b0);
    if (s0 > b + 1e-9) break;
    const len = meterAtBar(p, bar).barLen;
    if (!step || grid === 'bar') { if (s0 >= a - 1e-9) out.push(s0); }
    else for (let k = 0; k * step < len - 1e-9; k++) { const s = secAt(p, b0 + k * step); if (s > b + 1e-9) break; if (s >= a - 1e-9) out.push(s); }
    bar++;
  }
  return out;
}
/** Klick-Zeitpunkte im Bereich [a, b): jeder Schlag der Taktart (Notenwert), betont auf der Eins */
export function clickTimes(p: TempoSource, a: number, b: number): { t: number; accent: boolean }[] {
  const out: { t: number; accent: boolean }[] = [];
  for (let bar = Math.max(0, Math.floor(barAtSec(p, Math.max(0, a)) - 1e-9)); out.length < 2000; bar++) {
    const b0 = barToBeat(p, bar);
    if (secAt(p, b0) >= b) break;
    const m = meterAtBar(p, bar);
    for (let k = 0; k < m.num; k++) { const t = secAt(p, b0 + k * 4 / m.den); if (t >= b) break; if (t >= a - 1e-9) out.push({ t, accent: k === 0 }); }
  }
  return out;
}
/** Auf das nächste Raster ziehen */
export function snapSec(p: TempoSource, sec: number, grid: string): number {
  const b = beatAt(p, sec), bar = Math.floor(beatToBar(p, b) + 1e-9), b0 = barToBeat(p, bar), len = meterAtBar(p, bar).barLen;
  const step = grid === 'bar' ? len : (GRID_BEATS[grid] ?? 0.25);
  let sb = b0 + Math.round((b - b0) / step) * step;
  if (sb > b0 + len - 1e-9) sb = b0 + len;
  return secAt(p, sb);
}
/** Rasterweite in Sekunden an einer Stelle (für Toleranzen und Nudge) */
export function gridSecAt(p: TempoSource, sec: number, grid: string): number {
  const beats = grid === 'bar' ? meterAt(p, sec).barLen : (GRID_BEATS[grid] ?? 0.25);
  return beats * spbAt(p, sec);
}

// ---------------------------------------------------------------------------
// Tempo ändern: MIDI-Clips, Marker, Akkorde und Loop bleiben auf ihren Takten, Audio bleibt zeitlich, wo es ist.
interface Retimable extends TempoSource {
  clips: { kind: string; start: number; duration: number; loop?: { length: number }; notes?: { t: number; d: number }[]; warp?: { points: { s: number; b: number }[] } }[];
  markers: { time: number; end?: number }[];
  chords: { time: number }[];
  loop: { start: number; end: number };
}
/**
 * Nach einer Tempoänderung angleichen.
 * 'bars' (Standard): MIDI, Marker, Akkorde und Loop bleiben auf ihren Takten (Tempo bewusst geändert).
 * 'time': alles bleibt zeitlich, wo es ist, nur das Raster ändert sich (Tempo aus einer Aufnahme ermittelt);
 *         MIDI-Noten werden dafür in die neuen Viertel umgerechnet.
 */
export function retime(p: Retimable, before: TempoSource, mode: 'bars' | 'time' = 'bars') {
  if (mode === 'time') {
    // Elastic-Audio-Clips: Warp-Punkte so umrechnen, dass jede Stelle der Aufnahme zeitlich bleibt
    for (const c of p.clips) {
      if (!c.warp) continue;
      const ob = beatAt(before, c.start), nb = beatAt(p, c.start);
      for (const w of c.warp.points) w.b = beatAt(p, secAt(before, ob + w.b)) - nb;
    }
    for (const c of p.clips) {
      if (c.kind !== 'midi' || !c.notes || c.loop) continue;
      const ob = beatAt(before, c.start), nb = beatAt(p, c.start);
      for (const n of c.notes) {
        const s = secAt(before, ob + n.t), e = secAt(before, ob + n.t + n.d);
        n.t = Math.round((beatAt(p, s) - nb) * 1e6) / 1e6; n.d = Math.max(1e-3, Math.round((beatAt(p, e) - beatAt(p, s)) * 1e6) / 1e6);
      }
    }
    return;
  }
  const map = (s: number) => secAt(p, beatAt(before, s));
  for (const c of p.clips) {
    // MIDI und Elastic Audio (tick-basiert) folgen den Takten; Warp-Punkte liegen in Vierteln und bleiben
    if (c.kind !== 'midi' && !c.warp) continue;
    const s = map(c.start), e = map(c.start + c.duration);
    if (c.loop && c.loop.length > 0) c.loop.length = map(c.start + c.loop.length) - s;
    c.start = s; c.duration = Math.max(1e-3, e - s);
  }
  for (const m of p.markers) { const e = m.end !== undefined ? map(m.end) : undefined; m.time = map(m.time); if (e !== undefined) m.end = e; }
  for (const ch of p.chords) ch.time = map(ch.time);
  const ls = map(p.loop.start), le = map(p.loop.end); p.loop.start = ls; p.loop.end = le;
}
/** Kopie der Tempo-Angaben (vor einer Änderung merken) */
export const tempoSnapshot = (p: TempoSource): TempoSource => ({ bpm: p.bpm, beatsPerBar: p.beatsPerBar, meterDen: p.meterDen, tempos: (p.tempos ?? []).map((e) => ({ ...e })), meters: (p.meters ?? []).map((e) => ({ ...e })) });

/** Tempoereignis setzen (beat 0 = Grundtempo) */
export function setTempo(p: TempoSource, beat: number, bpm: number) {
  if (beat <= 1e-9) { p.bpm = bpm; return; }
  const list = (p.tempos ??= []);
  const e = list.find((x) => Math.abs(x.beat - beat) < 1e-6);
  if (e) e.bpm = bpm; else list.push({ beat, bpm });
  list.sort((a, b) => a.beat - b.beat);
}
export function removeTempo(p: TempoSource, beat: number) { p.tempos = (p.tempos ?? []).filter((x) => Math.abs(x.beat - beat) > 1e-6); }
/** Taktart ab Takt (0-basiert) setzen */
export function setMeter(p: TempoSource, bar: number, num: number, den: number) {
  if (bar <= 0) { p.beatsPerBar = num; p.meterDen = den; return; }
  const list = (p.meters ??= []);
  const e = list.find((x) => x.bar === bar);
  if (e) { e.num = num; e.den = den; } else list.push({ bar, num, den });
  list.sort((a, b) => a.bar - b.bar);
}
export function removeMeter(p: TempoSource, bar: number) { p.meters = (p.meters ?? []).filter((x) => x.bar !== bar); }
/** Alle Tempoereignisse inkl. Grundtempo, für Anzeige und Liste */
export function tempoEvents(p: TempoSource): { beat: number; bpm: number; sec: number }[] { return mapOf(p).t.map((s) => ({ beat: s.beat, bpm: Math.round(6000 / s.spb) / 100, sec: s.sec })); }
export function meterEvents(p: TempoSource): { bar: number; num: number; den: number; sec: number }[] { return mapOf(p).m.map((m) => ({ bar: m.bar, num: m.num, den: m.den, sec: secAt(p, m.beat) })); }
